/**
 * HVP Toast — minimal, accessible, auto-dismissing notification.
 *
 * Used by the cascade controller to confirm a silent recovery: when the
 * no-match server re-check finds a variation the (stale) client dataset
 * missed, we apply it and toast "Options refreshed" instead of dead-ending.
 * Dependency-free; styled via .hvp-toast in hvp-variations.css (accent shared
 * with the no-match modal via --hvp-nm-accent).
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

const TOAST_CLASS = 'hvp-toast';

/**
 * Show a transient toast. Reuses a single live region so rapid calls don't
 * stack. Auto-dismisses; CSS handles the reduced-motion case.
 *
 * @param {string} message            Text to display (textContent — XSS-safe).
 * @param {Object} [opts]
 * @param {number} [opts.duration=4200] Visible time in ms before auto-dismiss.
 */
export function showToast(message, opts = {}) {
    if (!message) {
        return;
    }
    const duration = typeof opts.duration === 'number' ? opts.duration : 4200;

    let el = document.querySelector('.' + TOAST_CLASS);
    if (!el) {
        el = document.createElement('div');
        el.className = TOAST_CLASS;
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        document.body.appendChild(el);
    }

    // Cancel any in-flight dismiss timer so the new message gets a full dwell.
    if (el._hvpTimer) {
        window.clearTimeout(el._hvpTimer);
    }

    el.textContent = message;
    // Force reflow so re-triggering the entrance transition works on a reused node.
    void el.offsetWidth;
    el.classList.add('is-visible');

    el._hvpTimer = window.setTimeout(() => {
        el.classList.remove('is-visible');
    }, duration);
}

export default showToast;
